<template>
  <div class="activity-container">
    <Card>
      <p slot="title">活动管理</p>

      <!-- 搜索区域 -->
      <div class="search-con">
        <Input
          v-model="searchParams.title"
          placeholder="请输入活动标题"
          style="width: 300px; margin-right: 10px"
          clearable
          @on-enter="handleSearch"
        >
          <span slot="prepend">标题</span>
        </Input>

        <Select
          v-model="searchParams.status"
          placeholder="请选择状态"
          style="width: 150px; margin-right: 10px"
          clearable
        >
          <Option value="0">草稿</Option>
          <Option value="1">发布</Option>
          <Option value="2">结束</Option>
        </Select>

        <DatePicker
          type="date"
          v-model="searchParams.start_date"
          placeholder="开始日期"
          style="width: 200px; margin-right: 10px"
          clearable
        />

        <DatePicker
          type="date"
          v-model="searchParams.end_date"
          placeholder="结束日期"
          style="width: 200px; margin-right: 10px"
          clearable
        />

        <Button type="primary" @click="handleSearch">搜索</Button>
        <Button type="success" @click="handleAdd" style="margin-left: 10px">新增活动</Button>
      </div>

      <!-- 活动列表 -->
      <div style="margin-top: 20px">
        <Table
          :columns="columns"
          :data="tableData"
          :loading="loading"
          border
          stripe
          @on-sort-change="handleSortChange"
        >
          <template slot-scope="{ row }" slot="action">
            <Button type="primary" size="small" @click="handleEdit(row)">编辑</Button>
            <Button
              type="warning"
              size="small"
              style="margin-left: 10px"
              @click="handleStatus(row)"
            >
              {{ row.status === 1 ? '结束' : row.status === 0 ? '发布' : '重新发布' }}
            </Button>
            <Button
              type="error"
              size="small"
              style="margin-left: 10px"
              @click="handleDelete(row)"
            >
              删除
            </Button>
          </template>

          <template slot-scope="{ row }" slot="status">
            <Tag :type="getStatusType(row.status)">
              {{ getStatusText(row.status) }}
            </Tag>
          </template>

          <template slot-scope="{ row }" slot="company_count">
            <Button type="link" size="small" @click="handleViewCompany(row.id)">
              {{ row.company_count || 0 }} 家企业
            </Button>
          </template>
        </Table>

        <!-- 分页 -->
        <div style="margin-top: 20px; text-align: right">
          <Page
            :total="total"
            :current="pageParams.page"
            :page-size="pageParams.limit"
            @on-change="handlePageChange"
            @on-page-size-change="handlePageSizeChange"
            show-sizer
            show-total
          />
        </div>
      </div>
    </Card>

    <!-- 新增/编辑弹窗 -->
    <Modal
      v-model="modalVisible"
      :title="modalTitle"
      width="800px"
      @on-ok="handleSubmit"
      @on-cancel="modalVisible = false"
    >
      <Form ref="activityForm" :model="formData" :rules="formRules">
        <FormItem label="活动标题" prop="title">
          <Input v-model="formData.title" placeholder="请输入活动标题" />
        </FormItem>

        <FormItem label="活动封面">
          <div class="cover-upload">
            <div class="demo-upload-list" v-if="formData.cover_image">
              <img :src="formData.cover_image">
              <div class="demo-upload-list-cover">
                <Icon type="ios-eye-outline" @click="handleViewCover" size="40"></Icon>
                <Icon type="ios-trash-outline" @click="handleRemoveCover" size="40"></Icon>
              </div>
            </div>

            <Upload
              v-else
              :show-upload-list="false"
              :on-success="handleCoverSuccess"
              :format="['jpg','jpeg','png']"
              :max-size="5120"
              :on-format-error="handleFormatError"
              :on-exceeded-size="handleMaxSize"
              :before-upload="handleBeforeUpload"
              name="up_img"
              type="drag"
              action="/v2/activity/upload"
              class="cover-uploader">
              <div class="upload-area">
                <Icon type="ios-camera" size="40"></Icon>
                <p>点击或拖拽上传封面图片</p>
              </div>
            </Upload>
          </div>
        </FormItem>

        <FormItem label="活动内容" prop="content">
          <TinyMCEEditor
            ref="myedit"
            width="100%"
            v-model="formData.content"
            @on-change="handleContentChange"
            @on-init="handleEditorInit"
          />
        </FormItem>

        <FormItem label="开始时间" prop="start_time">
          <DatePicker
            type="datetime"
            v-model="formData.start_time"
            placeholder="请选择开始时间"
            style="width: 100%"
          />
        </FormItem>

        <FormItem label="结束时间" prop="end_time">
          <DatePicker
            type="datetime"
            v-model="formData.end_time"
            placeholder="请选择结束时间"
            style="width: 100%"
          />
        </FormItem>

        <FormItem label="状态" prop="status">
          <Select v-model="formData.status">
            <Option v-for="item in statusList" :value="item.value" :key="item.value">
              {{ item.meaning }}
            </Option>
          </Select>
        </FormItem>

        <FormItem label="展示形式" prop="display_type">
          <Select v-model="formData.display_type">
            <Option :value="1">图片+内容</Option>
            <Option :value="2">仅内容</Option>
            <Option :value="3">仅图片</Option>
          </Select>
        </FormItem>
      </Form>

      <div slot="footer">
        <Button @click="modalVisible = false">取消</Button>
        <Button type="primary" @click="handleSubmit">确定</Button>
      </div>
    </Modal>

    <!-- 图片预览Modal -->
    <Modal title="查看封面" v-model="imageVisible" :width="600">
      <img :src="previewImage" style="width: 100%">
    </Modal>
  </div>
</template>

<script>
import {
  getActivityList,
  getActivityDetail,
  addActivity,
  updateActivity,
  deleteActivity
} from '@/api/activity.js'
import { getsysparameterList } from '@/api/system/sys_parameter'
import TinyMCEEditor from '_c/editor/TinyMCEEditor.vue'

export default {
  name: 'ActivityList',
  components: { TinyMCEEditor },
  data () {
    return {
      loading: false,
      tableData: [],
      total: 0,

      // 状态选择框数据，先从接口获取，接口异常再使用本地硬编码
      statusList: [
        { value: '0', meaning: '草稿' },
        { value: '1', meaning: '发布' },
        { value: '2', meaning: '结束' }
      ],

      searchParams: {
        title: '',
        status: '',
        start_date: '',
        end_date: ''
      },

      pageParams: {
        page: 1,
        limit: 10
      },

      modalVisible: false,
      modalTitle: '',
      formData: {
        id: null,
        title: '',
        cover_image: '',
        content: '',
        start_time: '',
        end_time: '',
        status: 0,
        display_type: 1
      },

      formRules: {
        title: [
          { required: true, message: '请输入活动标题', trigger: 'blur' },
          { min: 2, max: 200, message: '标题长度在 2 到 200 个字符', trigger: 'blur' }
        ],
        content: [
          { required: true, message: '请输入活动内容', trigger: 'blur' },
          { min: 10, max: 5000, message: '内容长度在 10 到 5000 个字符', trigger: 'blur' }
        ],
        start_time: [
          { validator: (rule, value, callback) => {
              if (!value) {
                callback()
              } else {
                callback()
              }
            },
message: '请选择开始时间',
trigger: 'change' }
        ],
        end_time: [
          { validator: (rule, value, callback) => {
              if (!value) {
                callback()
              } else {
                callback()
              }
            },
message: '请选择结束时间',
trigger: 'change' }
        ]
      },

      imageVisible: false,
      previewImage: '',

      columns: [
        { title: 'ID', key: 'id', width: 80, sortable: true },
        { title: '活动标题', key: 'title', width: 250, sortable: true },
        {
          title: '封面图片',
          key: 'cover_image',
          width: 100,
          render: (h, params) => {
            if (params.row.cover_image) {
              return h('img', {
                attrs: {
                  src: params.row.cover_image,
                  style: 'width: 60px; height: 60px; border-radius: 4px;'
                },
                on: {
                  click: () => {
                    this.previewImage = params.row.cover_image
                    this.imageVisible = true
                  }
                }
              })
            } else {
              return h('span', '无')
            }
          }
        },
        { title: '展示形式',
key: 'display_type',
width: 120,
          render: (h, params) => {
            const displayTypeMap = {
              1: '图片+内容',
              2: '仅内容',
              3: '仅图片'
            }
            return h('Tag', displayTypeMap[params.row.display_type] || '图片+内容')
          }
        },
        { title: '开始时间', key: 'start_time', width: 180, sortable: true },
        { title: '结束时间', key: 'end_time', width: 180, sortable: true },
        { title: '状态', slot: 'status', width: 100 },
        { title: '参与企业数', slot: 'company_count', width: 120 },
        { title: '操作', slot: 'action', width: 250, fixed: 'right' }
      ]
    }
  },

  mounted () {
    this.getStatusList()
    this.getActivityList()
  },

  methods: {
    // 获取状态列表
    async getStatusList () {
      try {
        const res = await getsysparameterList('activity', 'status', '', '', '', '')
        if (res.data.code === 0 && res.data.data.length > 0) {
          this.statusList = res.data.data.map(item => ({
            value: item.value,
            meaning: item.meaning
          }))
        } else {
          // 如果未获取到数据，使用默认硬编码的状态列表
          this.statusList = [
            { value: '0', meaning: '草稿' },
            { value: '1', meaning: '发布' },
            { value: '2', meaning: '结束' }
          ]
        }
      } catch (error) {
        console.error('获取状态参数失败:', error)
        // 使用默认硬编码的状态列表
        this.statusList = [
          { value: '0', meaning: '草稿' },
          { value: '1', meaning: '发布' },
          { value: '2', meaning: '结束' }
        ]
      }
    },

    // 获取活动列表
    async getActivityList () {
      this.loading = true
      try {
        const params = {
          ...this.searchParams,
          page: this.pageParams.page,
          limit: this.pageParams.limit
        }
        const res = await getActivityList(params)
        if (res.data.code === 0) {
          this.tableData = res.data.data.list || []
          this.total = res.data.data.total || 0
        } else {
          this.$Message.error(res.data.msg || '获取活动列表失败')
        }
      } catch (error) {
        this.$Message.error('获取活动列表失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch () {
      this.pageParams.page = 1
      this.getActivityList()
    },

    // 排序
    handleSortChange ({ column, key, order }) {
      if (order === 'asc') {
        this.tableData.sort((a, b) => a[key] > b[key] ? 1 : -1)
      } else if (order === 'desc') {
        this.tableData.sort((a, b) => a[key] < b[key] ? 1 : -1)
      }
    },

    // 分页
    handlePageChange (page) {
      this.pageParams.page = page
      this.getActivityList()
    },

    handlePageSizeChange (pageSize) {
      this.pageParams.limit = pageSize
      this.pageParams.page = 1
      this.getActivityList()
    },

    // 新增
    handleAdd () {
      this.modalTitle = '新增活动'
      this.formData = {
        id: null,
        title: '',
        cover_image: '',
        content: '',
        start_time: '',
        end_time: '',
        status: 0,
        display_type: 1
      }
      this.modalVisible = true
    },

    // 编辑
    async handleEdit (row) {
      this.modalTitle = '编辑活动'
      try {
        const res = await getActivityDetail(row.id)
        if (res.data.code === 0) {
          this.formData = res.data.data
          this.modalVisible = true
        } else {
          this.$Message.error(res.data.msg || '获取活动详情失败')
        }
      } catch (error) {
        this.$Message.error('获取活动详情失败')
        console.error(error)
      }
    },

    // 状态操作
    handleStatus (row) {
      const newStatus = row.status === 1 ? 2 : (row.status === 0 ? 1 : 1)
      const operationText = newStatus === 1 ? '发布' : newStatus === 2 ? '结束' : '重新发布'

      this.$Modal.confirm({
        title: '确认操作',
        content: `确定要${operationText}该活动吗？`,
        onOk: async () => {
          try {
            const res = await updateActivity({
              ...row,
              status: newStatus
            })
            if (res.data.code === 0) {
              this.$Message.success('操作成功')
              this.getActivityList()
            } else {
              this.$Message.error(res.data.msg || '操作失败')
            }
          } catch (error) {
            this.$Message.error('操作失败')
            console.error(error)
          }
        }
      })
    },

    // 删除
    handleDelete (row) {
      this.$Modal.confirm({
        title: '确认删除',
        content: '确定要删除该活动吗？删除后相关数据将无法恢复。',
        onOk: async () => {
          try {
            const res = await deleteActivity(row.id)
            if (res.data.code === 0) {
              this.$Message.success('删除成功')
              this.getActivityList()
            } else {
              this.$Message.error(res.data.msg || '删除失败')
            }
          } catch (error) {
            this.$Message.error('删除失败')
            console.error(error)
          }
        }
      })
    },

    // 查看企业列表
    handleViewCompany (activityId) {
      // 跳转到企业管理页面
      this.$router.push(`/activity/company/list/${activityId}`)
    },
 handleEditorInit (editor) {
      this.editor = editor
    },
    // 处理编辑器内容变化
    handleContentChange (html, text) {
      this.formValidate.content = html
    },
    // 表单提交
    async handleSubmit () {
      try {
        this.$refs.activityForm.validate(async (valid) => {
          if (valid) {
            let res
            if (this.formData.id) {
              res = await updateActivity(this.formData)
            } else {
              res = await addActivity(this.formData)
            }

            if (res.data.code === 0) {
              this.$Message.success(this.formData.id ? '更新成功' : '新增成功')
              this.modalVisible = false
              this.getActivityList()
            } else {
              this.$Message.error(res.data.msg || '操作失败')
            }
          }
        })
      } catch (error) {
        this.$Message.error('操作失败')
        console.error(error)
      }
    },

    // 获取状态文本
    getStatusText (status) {
      const statusMap = {
        '0': '草稿',
        '1': '发布',
        '2': '结束'
      }
      return statusMap[status] || '未知'
    },

    // 获取状态类型
    getStatusType (status) {
      const statusMap = {
        '0': 'default',
        '1': 'success',
        '2': 'error'
      }
      return statusMap[status] || 'default'
    },

    // 图片上传相关方法
    handleViewCover () {
      this.previewImage = this.formData.cover_image
      this.imageVisible = true
    },

    handleRemoveCover () {
      this.formData.cover_image = ''
    },

    handleBeforeUpload (config) {
      return new Promise((resolve) => {
        // 生成动态授权头
        const now = new Date()
        const hours = now.getHours().toString().padStart(2, '0')
        const minutes = now.getMinutes().toString().padStart(2, '0')
        const month = (now.getMonth() + 1).toString().padStart(2, '0')
        const day = now.getDate().toString().padStart(2, '0')
        const year = now.getFullYear()

        const rawString = `zgkd_upimage_${hours}${minutes}${month}${day}${year}`
        const base64String = btoa(rawString)

        this.up_headers = {
          'Authorization': base64String
        }

        // 确保 Vue 响应式更新完成
        this.$nextTick(() => {
          this.spinShow = true // 显示加载状态
          resolve(true) // 允许上传
        })
      })
    },

    handleCoverSuccess (res, file) {
      this.spinShow = false
      if (res.code === 0) {
        let imgSrc = res.data.path
        file.url = imgSrc
        this.formData.cover_image = imgSrc
        this.formData.cover_image_id = res.data.fileid
      } else {
        this.$Message.error(`${res.msg}`)
      }
    },

    handleFormatError (file) {
      this.spinShow = false
      this.$Notice.warning({
        title: '文件格式不正确',
        desc: '文件 ' + file.name + ' 格式不正确，请上传 jpg 或 png 格式的图片。'
      })
    },

    handleMaxSize (file) {
      this.spinShow = false
      this.$Notice.warning({
        title: '超出文件大小限制',
        desc: '文件 ' + file.name + ' 太大，不能超过 5M。'
      })
    }
  }
}
</script>

<style scoped>
.activity-container {
  padding: 10px;
}

.search-con {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.cover-upload {
  .demo-upload-list {
    display: inline-block;
    width: 200px;
    height: 120px;
    text-align: center;
    line-height: 120px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    overflow: hidden;
    background: #fafafa;
    position: relative;
    box-shadow: 0 2px 8px rgba(0,0,0,.08);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .demo-upload-list-cover {
      display: none;
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background: rgba(0,0,0,.6);

      i {
        color: #fff;
        font-size: 20px;
        cursor: pointer;
        margin: 0 8px;
        line-height: 120px;

        &:hover {
          color: #2d8cf0;
        }
      }
    }

    &:hover .demo-upload-list-cover {
      display: block;
    }
  }

  .cover-uploader {
    /deep/ .ivu-upload {
      width: 200px;
      height: 120px;

      .ivu-upload-drag {
        width: 100%;
        height: 100%;
        border: 2px dashed #d9d9d9;
        border-radius: 6px;
        background: #fafafa;
        transition: all 0.3s;

        &:hover {
          border-color: #2d8cf0;
          background: rgba(45, 140, 240, 0.05);
        }

        .upload-area {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #666;

          i {
            margin-bottom: 8px;
            color: #999;
          }

          p {
            margin: 0;
            font-size: 12px;
            color: #999;
          }
        }
      }
    }
  }
}
</style>
